import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Checkbox, ConfigProvider, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import UsPhoneInput from 'components/form/USPhoneInput';
import s from './s.module.less';
import type { SignUpFormData } from '../../types';
import PasswordValidation, { getPasswordErrors, isPasswordValid, PasswordError } from './PasswordValidation';
import { registerAndLogin, ERegisterType } from 'api/public';
import { sendLeadFormCode, verifyLeadFormCode } from 'api/ppaLeadForm';
import { processAuthResponse } from 'utils/auth';
import { buildUtmObject, getHelloKlarityHost } from 'utils/common';
import ProviderStore from 'store/Provider';
import { trackHeapEvent } from 'utils/analytics';
import { encryptString } from 'utils/encrypt';

const RESEND_SECONDS = 60;

interface SignUpFormProps {
    initialValues?: Partial<SignUpFormData>;
    onSubmit?: (values: SignUpFormData) => void;
    onChangeEmail?: () => void;
    emailDisabled?: boolean;
    isCredentialing?: boolean;
}

export default function SignUpForm({
    initialValues,
    onSubmit,
    onChangeEmail,
    emailDisabled = true,
    isCredentialing = false,
}: SignUpFormProps) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<PasswordError[]>([]);
    const [, set] = ProviderStore.useStore();

    const [phoneReadOnly, setPhoneReadOnly] = useState(true);

    // 邮箱验证码这一步：验证通过后才展示 phone / password / Create account
    const [verified, setVerified] = useState(false);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // 防止 StrictMode 下 effect 二次执行导致重复发送验证码
    const codeSentRef = useRef(false);

    const email = initialValues?.email || '';

    const startCountdown = () => {
        setResendSeconds(RESEND_SECONDS);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setResendSeconds((t) => {
                if (t <= 1) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    return 0;
                }
                return t - 1;
            });
        }, 1000);
    };

    // 进入激活卡片时发送验证码并开始倒计时
    useEffect(() => {
        if (codeSentRef.current) return;
        codeSentRef.current = true;
        if (email) sendLeadFormCode(email);
        startCountdown();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResend = async () => {
        if (!email) return;
        const res: any = await sendLeadFormCode(email);
        if (res?.data?.code === 200) {
            setCode('');
            setCodeError(false);
            message.success('A new code is on its way');
            startCountdown();
        } else if (res?.errorCode === 1049) {
            message.error("You've reached the resend limit. Please try again later.");
        } else {
            message.error(res?.error || 'Failed to resend the code. Please try again.');
        }
    };

    const handleVerify = async () => {
        if (code.length !== 6) {
            setCodeError(true);
            return;
        }
        try {
            setVerifying(true);
            const res: any = await verifyLeadFormCode(email, code);
            if (res?.data?.code === 200 && res?.data?.data === true) {
                setVerified(true);
                setTimeout(() => {
                    setPhoneReadOnly(false);
                    form.setFieldsValue({ phoneFe: '' });
                }, 500);
                if (timerRef.current) clearInterval(timerRef.current);
                return;
            }
            setCodeError(true);
            if (res?.errorCode === 1047) {
                message.error('This code has expired. Please request a new one.');
            } else if (res?.errorCode === 1048) {
                message.error('Too many incorrect attempts. Please request a new code.');
            } else {
                message.error(res?.error || 'Invalid verification code');
            }
        } catch (e) {
            setCodeError(true);
            message.error('Invalid verification code');
        } finally {
            setVerifying(false);
        }
    };

    // 计算是否显示密码错误
    const showPasswordError = password && passwordErrors.length > 0;

    const handleSubmit = async (values: SignUpFormData) => {
        try {
            setLoading(true);

            // 注册尝试埋点
            trackHeapEvent('WebApp - PPA Sign Up - Click - Registration Attempt', {
                em: await encryptString(values.email),
                hasPhone: !!values.phoneFe,
                subscribeToNewsletter: values.subscribeToNewsletter,
            });

            // 将表单数据转换为API所需的格式
            const apiData = {
                email: values.email,
                password: values.password,
                type: ERegisterType.KLARITY_PORTAL,
                inform: values.subscribeToNewsletter ? (1 as const) : (0 as const),
                tel: parseInt(values.phoneFe, 10) || 0,
                utm: buildUtmObject(),
                signUpFrom: 'KLARITY_ONBOARDING',
                verifyCode: code, // step1 校验通过的 OTP，注册时带上
                isLogin: false,
            };

            const result = await registerAndLogin(apiData);

            const authResult = processAuthResponse(result, set, ERegisterType.KLARITY_PORTAL);

            if (authResult) {
                // 注册成功埋点
                trackHeapEvent('WebApp - PPA Sign Up - View - Registration Success', {
                    em: await encryptString(values.email),
                    subscribeToNewsletter: values.subscribeToNewsletter,
                });
                message.success('Registered successfully');
                onSubmit?.(values);
            } else {
                // 注册失败埋点
                trackHeapEvent('WebApp - PPA Sign Up - View - Registration Failed', {
                    em: await encryptString(values.email),
                    reason: 'auth_failed',
                    error: result.error,
                });
                message.error(result.error);
            }
        } catch (error) {
            // 注册错误埋点
            trackHeapEvent('WebApp - PPA Sign Up - View - Registration Error', {
                em: await encryptString(values.email),
                errorType: 'api_error',
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            message.error('Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={s.formContainer}>
            <Form
                form={form}
                onFinish={handleSubmit}
                initialValues={{
                    email: '',
                    phoneFe: '',
                    agreeToTerms: true,
                    subscribeToNewsletter: true,
                    ...initialValues,
                }}
                requiredMark={false}
                layout="vertical"
                className={s.signUpForm}
                autoComplete="off"
            >
                {/* 表单头部 */}
                <div className={s.formHeader}>
                    <h2 className={s.formTitle}>Activate your account</h2>
                    <p className={s.formSubtitle}>
                        {!verified && <>Enter the code we emailed you.</>}
                        {verified && isCredentialing && (
                            <>
                                We&apos;ve sent a verification email. Please use an email you check regularly. Phone
                                numbers are stored for records only.
                            </>
                        )}
                        {verified && !isCredentialing && (
                            <>
                                We&apos;ve sent a verification email to your inbox. Please make sure to use an email you
                                check regularly.
                            </>
                        )}
                    </p>
                </div>

                <div className={s.formBody}>
                    {!verified && email && (
                        <div className={s.codeSentAlert}>
                            <svg className={s.codeSentIcon} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span className={s.codeSentText}>
                                Code sent to <strong>{email}</strong>. If you didn&apos;t receive it, check your spam
                                folder or resend below.
                            </span>
                        </div>
                    )}
                    <Form.Item
                        name="email"
                        label={
                            <span className={s.inputLabel}>
                                Email address <span className={s.required}>*</span>
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: 'Please input your email!',
                            },
                            {
                                type: 'email',
                                message: 'Please input a valid email!',
                            },
                        ]}
                    >
                        <Input
                            size="large"
                            placeholder="Enter an email you check regularly"
                            className={s.input}
                            disabled={emailDisabled}
                        />
                    </Form.Item>
                    {emailDisabled && (
                        <Button
                            type="link"
                            className={s.changeEmailBtn}
                            onClick={async () => {
                                // 更换邮箱按钮埋点
                                trackHeapEvent('WebApp - PPA Sign Up - Click - Change Email Button', {
                                    fromForm: true,
                                });
                                onChangeEmail?.();
                            }}
                        >
                            Change email
                        </Button>
                    )}

                    {!verified && (
                        <div className={s.verifyBlock}>
                            <div className={s.inputLabel}>
                                Verification code <span className={s.required}>*</span>
                            </div>
                            <Input
                                size="large"
                                className={s.input}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                placeholder="Enter 6-digit code"
                                value={code}
                                status={codeError ? 'error' : undefined}
                                onChange={(e) => {
                                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                    setCodeError(false);
                                }}
                                onPressEnter={handleVerify}
                            />
                            <p className={s.verifyHelper}>
                                We emailed a 6-digit code to the address above. Codes expire after 10 minutes.
                            </p>
                            <p className={s.resendLine}>
                                Didn&apos;t get the code?&nbsp;
                                <button type="button" className={s.resendLink} onClick={handleResend}>
                                    {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend'}
                                </button>
                            </p>
                            <Button
                                type="primary"
                                size="large"
                                loading={verifying}
                                disabled={code.length !== 6}
                                className={s.submitButton}
                                block
                                onClick={handleVerify}
                            >
                                Verify email
                            </Button>
                        </div>
                    )}

                    {verified && (
                        <>
                            <Form.Item
                                name="phoneFe"
                                label={
                                    <span className={s.inputLabel}>
                                        Phone number <span className={s.required}>*</span>
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter your phone number.',
                                    },
                                ]}
                            >
                                <UsPhoneInput
                                    size="large"
                                    placeholder="Enter your phone number"
                                    className={s.input}
                                    customPaddingLeft={6}
                                    readOnly={phoneReadOnly}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label={
                                    <span className={s.inputLabel}>
                                        Password <span className={s.required}>*</span>
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input your password!',
                                    },
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.resolve();
                                            }
                                            if (isPasswordValid(value)) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Password does not meet requirements'));
                                        },
                                    },
                                ]}
                            >
                                <Input.Password
                                    size="large"
                                    placeholder="Enter your password"
                                    className={s.passwordInput}
                                    status={showPasswordError ? 'error' : undefined}
                                    value={password}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setPassword(value);
                                        setPasswordErrors(getPasswordErrors(value));
                                        form.setFieldsValue({ password: value });
                                    }}
                                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            {/* 密码校验指示器 */}
                            <PasswordValidation password={password} visible={!!password} />
                        </>
                    )}
                </div>

                {/* 表单操作区（验证通过后展示） */}
                {verified && (
                    <div className={s.formActions}>
                        {/* 提交按钮 */}
                        <Form.Item
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.email !== currentValues.email ||
                                prevValues.phoneFe !== currentValues.phoneFe ||
                                prevValues.password !== currentValues.password ||
                                prevValues.agreeToTerms !== currentValues.agreeToTerms
                            }
                        >
                            {({ getFieldsValue }) => {
                                const {
                                    email: formEmail,
                                    phoneFe,
                                    password: formPassword,
                                    agreeToTerms,
                                } = getFieldsValue();

                                // 检查密码是否有效
                                const isPasswordValidValue = formPassword && isPasswordValid(formPassword);

                                // 检查所有必填字段和验证条件
                                const isFormValid =
                                    formEmail && phoneFe && formPassword && isPasswordValidValue && agreeToTerms;

                                return (
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        loading={loading}
                                        disabled={!isFormValid}
                                        className={s.submitButton}
                                        block
                                    >
                                        Create account
                                    </Button>
                                );
                            }}
                        </Form.Item>

                        <div className={s.checkboxGroup}>
                            <ConfigProvider
                                theme={{
                                    token: {
                                        colorPrimary: '#0E62F4',
                                    },
                                }}
                            >
                                <Form.Item
                                    name="agreeToTerms"
                                    valuePropName="checked"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Please agree to the terms'));
                                            },
                                        },
                                    ]}
                                >
                                    <Checkbox
                                        className={s.checkbox}
                                        onChange={async (e) => {
                                            // 同意条款埋点
                                            trackHeapEvent('WebApp - PPA Sign Up - Click - Agree To Terms', {
                                                checked: e.target.checked,
                                            });
                                        }}
                                    >
                                        <span className={s.checkboxLabel}>
                                            By clicking on &apos;Create account&apos; above, you agree to the{' '}
                                            <a
                                                href={`${getHelloKlarityHost()}/provider-terms-conditions`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={s.link}
                                            >
                                                Klarity Health Provider Agreement
                                            </a>{' '}
                                            .
                                        </span>
                                    </Checkbox>
                                </Form.Item>

                                <Form.Item name="subscribeToNewsletter" valuePropName="checked">
                                    <Checkbox
                                        className={s.checkbox}
                                        onChange={async (e) => {
                                            // 订阅新闻通讯埋点
                                            trackHeapEvent('WebApp - PPA Sign Up - Click - Subscribe Newsletter', {
                                                checked: e.target.checked,
                                            });
                                        }}
                                    >
                                        <span className={s.checkboxLabel}>
                                            Don&apos;t miss out—subscribe to Klarity&apos;s monthly Provider Resources
                                            Newsletter for vital compliance updates, platform announcements, expert best
                                            practice tips, and valuable CE resources—all conveniently delivered to help
                                            you grow your practice and stay informed.
                                        </span>
                                    </Checkbox>
                                </Form.Item>
                            </ConfigProvider>
                        </div>
                    </div>
                )}
            </Form>
        </div>
    );
}
